import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaExchangeAlt } from 'react-icons/fa';
import '../App.css'; // Upravený import na App.css

const P2PTrading = ({ provider, account }) => {
  const isInDevelopment = true; // Změň na false, až bude komponenta připravená

  const [orderBook, setOrderBook] = useState({ bids: [], asks: [] });
  const [balances, setBalances] = useState({ USDT: 0.0289, BTC: 0 });
  const [ownerFees, setOwnerFees] = useState(0);
  const [price, setPrice] = useState(0);
  const [p2pOrders, setP2pOrders] = useState([]);
  const [trade, setTrade] = useState({
    type: 'buy',
    amount: 0,
    price: 0,
    baseCurrency: 'BTC',
    quoteCurrency: 'USDT',
  });

  const tradingConfig = {
    pairs: ['BTC/USDT'],
    fees: { percentage: 0.0001 }, // 0.01% poplatek
  };

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd');
        setPrice(response.data.bitcoin.usd);
      } catch (error) {
        console.error('Error fetching price:', error);
      }
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 60000);
    return () => clearInterval(interval);
  }, []);

  const addOrder = () => {
    if (isInDevelopment) return; // Zabraňuje spuštění v režimu vývoje
    if (trade.amount <= 0 || trade.price <= 0) {
      alert('Cena a množství musí být větší než 0');
      return;
    }
    const total = trade.price * trade.amount;
    const hasFunds =
      trade.type === 'buy'
        ? balances[trade.quoteCurrency] >= total
        : balances[trade.baseCurrency] >= trade.amount;

    if (!hasFunds) {
      alert('Nedostatek prostředků');
      return;
    }

    const newOrder = {
      type: trade.type,
      amount: trade.amount,
      price: trade.price,
      baseCurrency: trade.baseCurrency,
      quoteCurrency: trade.quoteCurrency,
      timestamp: Date.now(),
    };
    setP2pOrders((prev) => [...prev, newOrder]);

    if (trade.type === 'buy') {
      setBalances((prev) => ({
        ...prev,
        [trade.quoteCurrency]: prev[trade.quoteCurrency] - total,
      }));
    } else {
      setBalances((prev) => ({
        ...prev,
        [trade.baseCurrency]: prev[trade.baseCurrency] - trade.amount,
      }));
    }

    const fee = total * tradingConfig.fees.percentage;
    setOwnerFees((prev) => prev + fee);
  };

  const matchP2pOrders = useCallback(() => {
    if (isInDevelopment) return; // Zabraňuje spuštění v režimu vývoje
    let bids = p2pOrders
      .filter((o) => o.type === 'buy' && o.baseCurrency === trade.baseCurrency && o.quoteCurrency === trade.quoteCurrency)
      .sort((a, b) => b.price - a.price);
    let asks = p2pOrders
      .filter((o) => o.type === 'sell' && o.baseCurrency === trade.baseCurrency && o.quoteCurrency === trade.quoteCurrency)
      .sort((a, b) => a.price - b.price);

    for (let bid of bids) {
      for (let ask of asks) {
        if (bid.price >= ask.price && bid.amount > 0 && ask.amount > 0) {
          const tradeAmount = Math.min(bid.amount, ask.amount);
          const tradeTotal = tradeAmount * ask.price;
          const fee = tradeTotal * tradingConfig.fees.percentage;

          setBalances((prev) => ({
            ...prev,
            [bid.quoteCurrency]: prev[bid.quoteCurrency] + tradeTotal - fee,
            [bid.baseCurrency]: prev[bid.baseCurrency] + tradeAmount,
          }));
          setOwnerFees((prev) => prev + fee);

          bid.amount -= tradeAmount;
          ask.amount -= tradeAmount;

          if (bid.amount <= 0) bids = bids.filter((b) => b !== bid);
          if (ask.amount <= 0) asks = asks.filter((a) => a !== ask);
        }
      }
    }
    setP2pOrders([...bids, ...asks]);
  }, [p2pOrders, trade.baseCurrency, trade.quoteCurrency, tradingConfig.fees.percentage, isInDevelopment]);

  useEffect(() => {
    setOrderBook({
      bids: p2pOrders
        .filter((o) => o.type === 'buy' && o.baseCurrency === trade.baseCurrency && o.quoteCurrency === trade.quoteCurrency)
        .sort((a, b) => b.price - a.price)
        .slice(0, 10),
      asks: p2pOrders
        .filter((o) => o.type === 'sell' && o.baseCurrency === trade.baseCurrency && o.quoteCurrency === trade.quoteCurrency)
        .sort((a, b) => a.price - b.price)
        .slice(0, 10),
    });
    matchP2pOrders();
  }, [p2pOrders, trade.baseCurrency, trade.quoteCurrency, matchP2pOrders]);

  return (
    <div className="p2p-container">
      <h2 className="governance">
        <FaExchangeAlt className="icon" /> P2P Obchodování
      </h2>
      <div className="flex gap-4">
        <div className="w-1/3 p2p-order-book">
          <table>
            <thead>
              <tr className="border-b border-black">
                <th className="p-2">Price ({trade.quoteCurrency})</th>
                <th className="p-2">Amount ({trade.baseCurrency})</th>
                <th className="p-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {orderBook.asks.map((order, i) => (
                <tr key={i} className="text-red-500">
                  <td className="p-2">{order.price.toFixed(2)}</td>
                  <td className="p-2">{order.amount.toFixed(4)}</td>
                  <td className="p-2">{(order.price * order.amount).toFixed(2)}</td>
                </tr>
              ))}
              {orderBook.bids.map((order, i) => (
                <tr key={i} className="text-green-500">
                  <td className="p-2">{order.price.toFixed(2)}</td>
                  <td className="p-2">{order.amount.toFixed(4)}</td>
                  <td className="p-2">{(order.price * order.amount).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="w-1/3 p2p-trade-panel">
          <div className="space-x-2 mb-4">
            <button
              onClick={() => setTrade({ ...trade, type: 'buy' })}
              className={`px-4 py-2 rounded ${trade.type === 'buy' ? 'bg-green-600' : 'bg-green-800'} hover:bg-green-700`}
              disabled={isInDevelopment}
            >
              Buy
            </button>
            <button
              onClick={() => setTrade({ ...trade, type: 'sell' })}
              className={`px-4 py-2 rounded ${trade.type === 'sell' ? 'bg-red-600' : 'bg-red-800'} hover:bg-red-700`}
              disabled={isInDevelopment}
            >
              Sell
            </button>
          </div>
          <div className="space-y-2">
            <select
              value={trade.baseCurrency}
              onChange={(e) =>
                setTrade({
                  ...trade,
                  baseCurrency: e.target.value,
                  quoteCurrency: e.target.value === 'BTC' ? 'USDT' : 'BTC',
                })
              }
              className="w-full p-2 bg-dark-gray border border-gold rounded"
              disabled={isInDevelopment}
            >
              <option value="BTC">BTC</option>
              <option value="USDT">USDT</option>
            </select>
            <select
              value={trade.quoteCurrency}
              onChange={(e) =>
                setTrade({
                  ...trade,
                  quoteCurrency: e.target.value,
                  baseCurrency: e.target.value === 'USDT' ? 'BTC' : 'USDT',
                })
              }
              className="w-full p-2 bg-dark-gray border border-gold rounded"
              disabled={isInDevelopment}
            >
              <option value="USDT">USDT</option>
              <option value="BTC">BTC</option>
            </select>
            <input
              type="number"
              value={trade.price || price}
              onChange={(e) => setTrade({ ...trade, price: parseFloat(e.target.value) || price })}
              placeholder={`Price (${trade.quoteCurrency})`}
              className="w-full p-2 bg-dark-gray border border-gold rounded"
              disabled={isInDevelopment}
            />
            <input
              type="number"
              value={trade.amount}
              onChange={(e) => setTrade({ ...trade, amount: parseFloat(e.target.value) || 0 })}
              placeholder={`Amount (${trade.baseCurrency})`}
              className="w-full p-2 bg-dark-gray border border-gold rounded"
              disabled={isInDevelopment}
            />
            <button
              onClick={addOrder}
              className="w-full py-2 bg-gold-light hover:bg-gold rounded"
              disabled={isInDevelopment}
            >
              Place Order
            </button>
          </div>
          <p className="mt-4">
            Available: {balances.USDT.toFixed(4)} {trade.quoteCurrency} | {balances.BTC.toFixed(8)} {trade.baseCurrency}
          </p>
        </div>

        <div className="w-1/3 p2p-assets">
          <h3>Assets</h3>
          <p>USDT: {balances.USDT.toFixed(4)}</p>
          <p>BTC: {balances.BTC.toFixed(8)}</p>
          <p>Owner Fees: {ownerFees.toFixed(8)} {trade.quoteCurrency}</p>
        </div>
      </div>

      {isInDevelopment && (
        <div className="development-overlay">
          <p>Funkce P2P obchodování je momentálně ve vývoji a není dostupná.</p>
        </div>
      )}
    </div>
  );
};

export default P2PTrading;